import { Card, Text, Group, Button, Stack, Divider, TextInput, Avatar } from '@mantine/core';
import { useAuth } from '@/contexts/auth.ctx.tsx';
import { DateTime } from 'luxon';
import { useState, useEffect } from 'react';
import { useFileDialog } from '@mantine/hooks';
import { useAppMeta } from '../../../hooks/app.meta.hook';
import { IconExternalLink, IconArrowLeft } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';

// Link to Stripe portal
// https://billing.stripe.com/p/login/test_fZu28raorbvlbVo55qbII00

interface AccountPanelProps {
    panelChangeReq: (_: string) => void;
}

export default function AccountPanel({ panelChangeReq }: AccountPanelProps) {
    const { accessor, injectHeader, updateAccessor, accessorCan } = useAuth();
    const meta = useAppMeta();
    const [srcImg, setSrcImg] = useState<string>('');
    const [newUpload, setNewUpload] = useState<File | null>(null);
    // Form state
    const [name, setName] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [phone, setPhone] = useState<string>('');
    const [website, setWebsite] = useState<string>('');

    useEffect(() => {
        const fetchImage = async () => {
            const req = new Request(`/api/${accessor?.id}/profile/img`, {
                method: 'GET'
            });
            const res = await fetch(req);
            if (res.status === 200) {
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                setSrcImg(url);
            }
        }
        setName(accessor?.name || '');
        setEmail(accessor?.email || '');
        setPhone(accessor?.phone || '');
        setWebsite(accessor?.website || '');
        // fetchImage();
    }, [accessor]);

    const fileDialog = useFileDialog({
        multiple: false,
        accept: 'image/*',
        onChange(files) {
            if (files && files.length > 0) {
                setNewUpload(files[0]);
                // Create a local URL for the selected image
                const imageUrl = URL.createObjectURL(files[0]);
                setSrcImg(imageUrl);
            }
        },
    });

    // Cleanup the object URL when component unmounts or when new image is selected
    useEffect(() => {
        return () => {
            if (srcImg) {
                URL.revokeObjectURL(srcImg);
            }
        };
    }, [srcImg]);


    const billingPortal = `https://billing.stripe.com/p/login/${meta?.stripeBP}`;

    const uploadImage = async () => {
        const formData = new FormData();
        formData.append('file', newUpload!);
        const req = new Request(`/api/auth/${accessor?.id}/profile/img`, {
            method: 'POST',
            body: formData
        });
        injectHeader(req);
        const res = await fetch(req);
        const data = await res.json();
        if (res.status === 200) {
            console.log('uploadImage::success');
            return;
        }
        console.log('uploadImage::res', data);
    }

    const deleteImage = async () => {
        const req = new Request(`/api/auth/${accessor?.id}/profile/img`, {
            method: 'DELETE'
        });
        injectHeader(req);
        const res = await fetch(req);
        const data = await res.json();
        if (res.status === 200) {
            console.log('deleteImage::success');
            return;
        }
        console.log('deleteImage::res', data);
    }

    return (
        <Card withBorder w={'80%'} mx={'auto'}>
            <Group justify='space-between'>
                <Text size={'lg'} fw={500}>Your Account</Text>
                <Group>
                    <Button 
                        disabled={name === accessor?.name && email === accessor?.email && phone === accessor?.phone && website === accessor?.website}
                        onClick={() => {
                            updateAccessor({ name, email, phone, website })
                                .then(() => notifications.show({
                                    title: 'Success',
                                    message: 'Account updated successfully',
                                    color: 'green',
                                }))
                                .catch(() => notifications.show({
                                    title: 'Error',
                                    message: 'Failed to update account',
                                    color: 'red',
                                }));
                        }}>Save</Button>
                </Group>
            </Group>
            <Divider my={'md'} />

            <Group justify='center' wrap='nowrap' mb={'md'}>
                <Stack style={{ display: 'none' }} justify='center' align='center' w={'45%'}>
                    <Avatar src={srcImg} size={'100px'} onClick={fileDialog.open} />
                    <Text size={'1.1rem'} c='dimmed'>Click the avatar to upload a new image</Text>
                    <Group wrap='nowrap'>
                        <Button variant='light' onClick={uploadImage} disabled={!newUpload}>Upload</Button>
                        <Button variant='light' onClick={deleteImage} disabled={!srcImg}>Delete</Button>
                    </Group>
                </Stack>
                <Divider orientation='vertical' />
                <Stack justify='center' align='center' w={'55%'} >
                    <TextInput label='Company Name' value={name} onChange={e => setName(e.target.value)} description="Shown to the user on the test page" w={'100%'} />
                    <TextInput label='Company Email' value={email} onChange={e => setEmail(e.target.value)} w={'100%'} description="For our records only" />
                    <TextInput label='Company Phone' value={phone} onChange={e => setPhone(e.target.value)} w={'100%'} description="For our records only" />
                    <TextInput label='Company Website' value={website} onChange={e => setWebsite(e.target.value)} w={'100%'} description="Shown to the user on the test page" />
                    <Divider my={'md'} />
                    <Text size={'1.1rem'} fw={300}>Billing Info</Text>
                    <Text>Renews On: {DateTime.fromSeconds(Number(accessor?.subEndsAt)).toLocaleString(DateTime.DATE_MED)}</Text>
                    <Text size={'1rem'} c='dimmed'>Subscription is managed by Stripe, use the button below to manage your subscription</Text>
                    <Button component='a' href={billingPortal} variant='light' fullWidth rightSection={<IconExternalLink />}>
                        Manage Subscription
                    </Button>
                </Stack>
            </Group>

        </Card>
    )
}